    <p-toast></p-toast>
    <app-page-shell>
      <app-page-hero eyebrow="HR Setup" title="Organization Setup" subtitle="Worker categories, departments, jobs, and positions used by HR employee profiles.">
        <div actions>
          <button pButton type="button" label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="load()"></button>
        </div>
      </app-page-hero>

      <app-hr-list-tabs
        [tabs]="[
          { key: 'categories', label: 'Worker Categories', icon: 'fa-solid fa-users-gear', badge: workerCategories.length },
          { key: 'departments', label: 'Departments', icon: 'fa-solid fa-sitemap', badge: departments.length },
          { key: 'jobs', label: 'Jobs', icon: 'fa-solid fa-briefcase', badge: jobs.length },
          { key: 'positions', label: 'Positions', icon: 'fa-solid fa-id-badge', badge: positions.length }
        ]"
        [(activeTab)]="activeListTab"
      ></app-hr-list-tabs>

      <section class="setup-grid">
        <app-page-data-panel [hidden]="activeListTab !== 'categories'" title="Worker Categories" subtitle="Tenant-managed classifications used across HR workflows" [resultText]="workerCategories.length + ' records'">
          @if (canManageOrg) {
            <div actions><button pButton type="button" label="New Worker Category" icon="fa-solid fa-plus" (click)="workerCategoryForm = { active: true }; openActionDialog('category')"></button></div>
          }
          @if (canManageOrg) {
          <app-hr-action-dialog [visible]="actionDialogVisible('category')" (visibleChange)="setActionDialogVisible('category', $event)" [title]="workerCategoryForm.workerCategoryId ? 'Edit Worker Category' : 'New Worker Category'" eyebrow="Organization Setup" subtitle="Maintain a tenant-managed workforce classification." icon="fa-solid fa-user-tag" [contextTitle]="workerCategoryForm.categoryName || workerCategoryForm.categoryCode || 'New category'" contextSubtitle="Worker categories are used across employee, payroll, readiness, and roster workflows.">
          <div class="setup-form">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="workerCategoryCode" pInputText [(ngModel)]="workerCategoryForm.categoryCode" [readonly]="!!workerCategoryForm.systemCategory" />
                <label for="workerCategoryCode">Code</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-user-tag"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="workerCategoryName" pInputText [(ngModel)]="workerCategoryForm.categoryName" />
                <label for="workerCategoryName">Category name</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="workerCategoryDescription" pTextarea [(ngModel)]="workerCategoryForm.description" rows="2"></textarea>
                <label for="workerCategoryDescription">Description</label>
              </p-iftaLabel>
            </p-inputGroup>
            <label class="active-row"><p-checkbox [(ngModel)]="workerCategoryForm.active" [binary]="true"></p-checkbox><span>Active</span></label>
            <button pButton type="button" label="Save Category" icon="pi pi-save" (click)="saveWorkerCategory()"></button>
            <button pButton type="button" label="Clear" severity="secondary" [outlined]="true" (click)="workerCategoryForm = { active: true }"></button>
          </div>
          </app-hr-action-dialog>
          }
          <p-table [value]="workerCategories" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header"><tr><th>Name</th><th>Code</th><th>Active</th><th></th></tr></ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>{{ item.categoryName }}</td>
                <td>{{ item.categoryCode }}</td>
                <td>{{ item.active ? 'Yes' : 'No' }}</td>
                <td>@if (canManageOrg) { <button pButton type="button" icon="pi pi-pencil" [text]="true" [rounded]="true" (click)="editWorkerCategory(item)"></button> }</td>
              </tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel [hidden]="activeListTab !== 'departments'" title="Departments" subtitle="Business units and operating teams" [resultText]="departments.length + ' records'">
          @if (canManageOrg) {
            <div actions><button pButton type="button" label="New Department" icon="fa-solid fa-plus" (click)="departmentForm = { active: true }; openActionDialog('department')"></button></div>
          }
          @if (canManageOrg) {
          <app-hr-action-dialog [visible]="actionDialogVisible('department')" (visibleChange)="setActionDialogVisible('department', $event)" [title]="departmentForm.departmentId ? 'Edit Department' : 'New Department'" eyebrow="Organization Setup" subtitle="Maintain a business unit or operating team." icon="fa-solid fa-building-user" [contextTitle]="departmentForm.departmentName || departmentForm.departmentCode || 'New department'" contextSubtitle="Departments support hierarchy, employee assignment, and accounting scope.">
          <div class="setup-form">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="departmentCode" pInputText [(ngModel)]="departmentForm.departmentCode" />
                <label for="departmentCode">Code</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-building-user"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="departmentName" pInputText [(ngModel)]="departmentForm.departmentName" />
                <label for="departmentName">Department name</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-sitemap"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="parentDepartmentId" [options]="departmentOptions" [(ngModel)]="departmentForm.parentDepartmentId" [showClear]="true" styleClass="w-full"></p-select>
                <label for="parentDepartmentId">Parent department</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="departmentDescription" pTextarea [(ngModel)]="departmentForm.description" rows="2"></textarea>
                <label for="departmentDescription">Description</label>
              </p-iftaLabel>
            </p-inputGroup>
            <label class="active-row"><p-checkbox [(ngModel)]="departmentForm.active" [binary]="true"></p-checkbox><span>Active</span></label>
            <button pButton type="button" label="Save Department" icon="pi pi-save" (click)="saveDepartment()"></button>
            <button pButton type="button" label="Clear" severity="secondary" [outlined]="true" (click)="departmentForm = { active: true }"></button>
          </div>
          </app-hr-action-dialog>
          }
          <p-table [value]="departments" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header"><tr><th>Name</th><th>Code</th><th>Active</th><th></th></tr></ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>{{ item.departmentName }}</td>
                <td>{{ item.departmentCode || '-' }}</td>
                <td>{{ item.active ? 'Yes' : 'No' }}</td>
                <td>
                  @if (canManageOrg) {
                    <button pButton type="button" icon="pi pi-pencil" [text]="true" [rounded]="true" (click)="editDepartment(item)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel [hidden]="activeListTab !== 'jobs'" title="Jobs" subtitle="Role families aligned to logistics work" [resultText]="jobs.length + ' records'">
          @if (canManageOrg) {
            <div actions><button pButton type="button" label="New Job" icon="fa-solid fa-plus" (click)="jobForm = { active: true }; openActionDialog('job')"></button></div>
          }
          @if (canManageOrg) {
          <app-hr-action-dialog [visible]="actionDialogVisible('job')" (visibleChange)="setActionDialogVisible('job', $event)" [title]="jobForm.jobId ? 'Edit Job' : 'New Job'" eyebrow="Organization Setup" subtitle="Maintain a role family aligned to workforce operations." icon="fa-solid fa-briefcase" [contextTitle]="jobForm.jobName || jobForm.jobCode || 'New job'" contextSubtitle="Jobs group responsibilities independently from concrete positions.">
          <div class="setup-form">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="jobCode" pInputText [(ngModel)]="jobForm.jobCode" />
                <label for="jobCode">Code</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-briefcase"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="jobName" pInputText [(ngModel)]="jobForm.jobName" />
                <label for="jobName">Job name</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-user-gear"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="jobWorkerCategory" [options]="categoryOptions" [(ngModel)]="jobForm.workerCategory" [showClear]="true" styleClass="w-full"></p-select>
                <label for="jobWorkerCategory">Worker category</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="jobDescription" pTextarea [(ngModel)]="jobForm.description" rows="2"></textarea>
                <label for="jobDescription">Description</label>
              </p-iftaLabel>
            </p-inputGroup>
            <label class="active-row"><p-checkbox [(ngModel)]="jobForm.active" [binary]="true"></p-checkbox><span>Active</span></label>
            <button pButton type="button" label="Save Job" icon="pi pi-save" (click)="saveJob()"></button>
            <button pButton type="button" label="Clear" severity="secondary" [outlined]="true" (click)="jobForm = { active: true }"></button>
          </div>
          </app-hr-action-dialog>
          }
          <p-table [value]="jobs" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header"><tr><th>Name</th><th>Category</th><th>Active</th><th></th></tr></ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>{{ item.jobName }}</td>
                <td>{{ format(item.workerCategory) }}</td>
                <td>{{ item.active ? 'Yes' : 'No' }}</td>
                <td>
                  @if (canManageOrg) {
                    <button pButton type="button" icon="pi pi-pencil" [text]="true" [rounded]="true" (click)="editJob(item)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>

        <app-page-data-panel [hidden]="activeListTab !== 'positions'" title="Positions" subtitle="Concrete seats employees can occupy" [resultText]="positions.length + ' records'">
          @if (canManageOrg) {
            <div actions><button pButton type="button" label="New Position" icon="fa-solid fa-plus" (click)="positionForm = { active: true }; openActionDialog('position')"></button></div>
          }
          @if (canManageOrg) {
          <app-hr-action-dialog [visible]="actionDialogVisible('position')" (visibleChange)="setActionDialogVisible('position', $event)" [title]="positionForm.positionId ? 'Edit Position' : 'New Position'" eyebrow="Organization Setup" subtitle="Maintain a concrete organizational seat." icon="fa-solid fa-id-badge" [contextTitle]="positionForm.positionName || positionForm.positionCode || 'New position'" contextSubtitle="Positions connect departments and jobs to employee assignments.">
          <div class="setup-form">
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="positionCode" pInputText [(ngModel)]="positionForm.positionCode" />
                <label for="positionCode">Code</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-id-badge"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="positionName" pInputText [(ngModel)]="positionForm.positionName" />
                <label for="positionName">Position name</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-building-user"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="positionDepartmentId" [options]="departmentOptions" [(ngModel)]="positionForm.departmentId" [showClear]="true" styleClass="w-full"></p-select>
                <label for="positionDepartmentId">Department</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-briefcase"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <p-select inputId="positionJobId" [options]="jobOptions" [(ngModel)]="positionForm.jobId" [showClear]="true" styleClass="w-full"></p-select>
                <label for="positionJobId">Job</label>
              </p-iftaLabel>
            </p-inputGroup>
            <p-inputGroup>
              <p-inputGroupAddon><i class="fa-solid fa-align-left"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <textarea id="positionDescription" pTextarea [(ngModel)]="positionForm.description" rows="2"></textarea>
                <label for="positionDescription">Description</label>
              </p-iftaLabel>
            </p-inputGroup>
            <label class="active-row"><p-checkbox [(ngModel)]="positionForm.active" [binary]="true"></p-checkbox><span>Active</span></label>
            <button pButton type="button" label="Save Position" icon="pi pi-save" (click)="savePosition()"></button>
            <button pButton type="button" label="Clear" severity="secondary" [outlined]="true" (click)="positionForm = { active: true }"></button>
          </div>
          </app-hr-action-dialog>
          }
          <p-table [value]="positions" styleClass="p-datatable-sm" responsiveLayout="scroll">
            <ng-template pTemplate="header"><tr><th>Name</th><th>Department</th><th>Job</th><th>Active</th><th></th></tr></ng-template>
            <ng-template pTemplate="body" let-item>
              <tr>
                <td>{{ item.positionName }}</td>
                <td>{{ item.departmentName || '-' }}</td>
                <td>{{ item.jobName || '-' }}</td>
                <td>{{ item.active ? 'Yes' : 'No' }}</td>
                <td>
                  @if (canManageOrg) {
                    <button pButton type="button" icon="pi pi-pencil" [text]="true" [rounded]="true" (click)="editPosition(item)"></button>
                  }
                </td>
              </tr>
            </ng-template>
          </p-table>
        </app-page-data-panel>
      </section>
    </app-page-shell>
